import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2, Truck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const empty = {
  ragione_sociale: "",
  partita_iva: "",
  codice_fiscale: "",
  nome_richiedente: "",
  telefono: "",
  email: "",
  sede_serbatoio: "",
  quantita: "",
  messaggio: "",
};

const NOTICE =
  "I prezzi del gasolio sono soggetti a variazioni giornaliere in base alle quotazioni di mercato. Il preventivo inviato sarà valido esclusivamente per la data di emissione. L'effettivo prezzo della merce sarà comunicato in seguito all'organizzazione logistica del viaggio.";

export default function QuoteForm({ category, tipo, slug, options }) {
  const [form, setForm] = useState(empty);
  const [prodotto, setProdotto] = useState(options?.[0] || "");
  const [privacy, setPrivacy] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [reference, setReference] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const finalTipo = options ? prodotto : tipo;
      const { data } = await api.post("/quote-requests", { category, tipo: finalTipo, privacy_consent: privacy, website, ...form });
      setReference(data.reference || "");
      setDone(true);
      toast.success("Richiesta inviata correttamente!");
      setForm(empty);
      setPrivacy(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <div
        data-testid={`quote-success-${slug}`}
        className="flex flex-col items-center text-center py-12 px-6"
      >
        <CheckCircle2 className="w-14 h-14 text-orange" strokeWidth={1.5} />
        <h4 className="font-display text-2xl mt-5 text-navy">Richiesta ricevuta</h4>
        {reference && (
          <p className="mt-4 bg-navy text-white px-4 py-2 text-sm" data-testid={`quote-reference-${slug}`}>
            Numero di riferimento: <span className="text-orange font-semibold">{reference}</span>
          </p>
        )}
        <p className="text-ink-soft/80 text-ink mt-3 max-w-sm">
          Grazie. Conserva il numero di riferimento: lo trovi anche nell'email di conferma. Il nostro
          ufficio commerciale ti ricontatterà al più presto con un preventivo dedicato.
        </p>
        <button
          data-testid={`quote-new-${slug}`}
          onClick={() => { setDone(false); setReference(""); }}
          className="mt-6 text-sm font-semibold uppercase tracking-[0.15em] text-orange hover:text-navy transition-colors"
        >
          Invia un'altra richiesta
        </button>
      </div>
    );

  return (
    <form onSubmit={submit} data-testid={`quote-form-${slug}`} className="space-y-5">
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        className="hidden"
        aria-hidden="true"
      />
      {category === "gasolio" && (
        <div className="border-l-4 border-orange bg-orange/5 p-4 space-y-2">
          <p className="text-[13px] leading-relaxed text-navy font-medium">
            <span className="uppercase tracking-wider text-orange font-semibold">
              Nota bene:{" "}
            </span>
            {NOTICE}
          </p>
          <p className="text-[13px] leading-relaxed text-navy font-semibold flex items-center gap-2">
            <Truck className="w-4 h-4 text-orange shrink-0" /> Consegna disponibile per la provincia di Teramo e dintorni.
          </p>
        </div>
      )}

      {category === "olii" && (
        <div className="border-l-4 border-orange bg-orange/5 p-4 space-y-2">
          <p className="text-[13px] leading-relaxed text-navy font-medium">
            <span className="uppercase tracking-wider text-orange font-semibold">Nota: </span>
            Per piccoli ordini è previsto il ritiro in sede.
          </p>
          <p className="text-[13px] leading-relaxed text-navy font-medium">
            Per le confezioni da un litro la quantità minima per il preventivo è di 12 litri, ovvero l'intero cartone.
          </p>
        </div>
      )}

      {category === "adblue" && (
        <div className="border-l-4 border-orange bg-orange/5 p-4">
          <p className="text-[13px] leading-relaxed text-navy font-medium">
            <span className="uppercase tracking-wider text-orange font-semibold">AdBlue: </span>
            Prodotti a marchio Brenntag. Consegna disponibile per la provincia di Teramo e dintorni. Per piccoli ordini (taniche) è previsto il ritiro in sede.
          </p>
        </div>
      )}

      {options && (
        <Field label="Tipo di fornitura AdBlue" required>
          <select
            required
            value={prodotto}
            onChange={(e) => setProdotto(e.target.value)}
            data-testid={`f-prodotto-${slug}`}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange"
          >
            {options.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </Field>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Ragione Sociale" required>
          <Input required value={form.ragione_sociale} onChange={set("ragione_sociale")} data-testid={`f-ragione-${slug}`} />
        </Field>
        <Field label="Nome del richiedente" required>
          <Input required value={form.nome_richiedente} onChange={set("nome_richiedente")} data-testid={`f-nome-${slug}`} />
        </Field>
        <Field label="Partita IVA" required={category !== "olii"}>
          <Input required={category !== "olii"} value={form.partita_iva} onChange={set("partita_iva")} data-testid={`f-piva-${slug}`} />
        </Field>
        <Field label="Codice Fiscale" required>
          <Input required value={form.codice_fiscale} onChange={set("codice_fiscale")} data-testid={`f-cf-${slug}`} />
        </Field>
        <Field label="Numero di Telefono" required>
          <Input required type="tel" value={form.telefono} onChange={set("telefono")} data-testid={`f-tel-${slug}`} />
        </Field>
        <Field label="Email" required>
          <Input required type="email" value={form.email} onChange={set("email")} data-testid={`f-email-${slug}`} />
        </Field>
      </div>

      <Field label={category === "olii" ? "Prodotti di interesse" : "Quantità stimata (litri)"}>
        <Input value={form.quantita} onChange={set("quantita")} data-testid={`f-qta-${slug}`} />
      </Field>
      {category === "gasolio" && (
        <Field label="Sede / Località del serbatoio da rifornire" required>
          <Input required value={form.sede_serbatoio} onChange={set("sede_serbatoio")} placeholder="Indirizzo del luogo di consegna" data-testid={`f-sede-${slug}`} />
        </Field>
      )}
      {category !== "gasolio" && (
        <Field label="Sede / Località di consegna">
          <Input value={form.sede_serbatoio} onChange={set("sede_serbatoio")} placeholder="Indirizzo del luogo di consegna o ritiro" data-testid={`f-sede-${slug}`} />
        </Field>
      )}
      <Field label="Messaggio (facoltativo)">
        <Textarea rows={3} value={form.messaggio} onChange={set("messaggio")} data-testid={`f-msg-${slug}`} />
      </Field>

      <label className="flex items-start gap-3 cursor-pointer" data-testid={`privacy-label-${slug}`}>
        <input
          type="checkbox"
          required
          checked={privacy}
          onChange={(e) => setPrivacy(e.target.checked)}
          data-testid={`privacy-checkbox-${slug}`}
          className="mt-1 h-4 w-4 shrink-0 accent-orange cursor-pointer"
        />
        <span className="text-[13px] leading-relaxed text-ink-soft">
          Dichiaro di aver letto la{" "}
          <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-orange font-medium hover:underline">
            Privacy Policy
          </a>{" "}
          e acconsento al trattamento dei dati per la finalità di preventivazione. <span className="text-orange">*</span>
        </span>
      </label>

      <motion.button
        whileTap={{ scale: 0.97 }}
        type="submit"
        disabled={loading}
        data-testid={`quote-submit-${slug}`}
        className="w-full bg-navy text-white h-12 flex items-center justify-center gap-2 font-semibold uppercase tracking-[0.12em] text-sm hover:bg-orange transition-colors disabled:opacity-60"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        Invia richiesta
      </motion.button>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ink-soft">
        {label} {required && <span className="text-orange">*</span>}
      </Label>
      {children}
    </div>
  );
}
